import { toMapFn } from "~/common/utils/reducers";
import type { AddressData, UnloadingPointData } from "~/features/cart/checkout/components/Address";
import { isCartPackEntryValid } from "~/features/cart/cart/redux/reducers/lineItemValidations";
import { isSurchargeIncludedInPriceValue } from "~/utils/features";
import * as WeightUtils from "~/utils/WeightUtils";
import { WeightUnit } from "~/utils/WeightUtils";
import { adjustFormat } from "~/utils/accountNumberUtils";
import dayjs from "dayjs";

import type {
  CartResponseDTO,
  PackageEntryDTO,
  EntryGroup,
  CreditCardDataDTO,
  B2BUnitDataDTO,
  PaymentTypeDTO,
  DeliveryModeDataDTO,
  UnloadingPointDTO,
  OrderType,
} from "../dto/CartResponseDTO";
import type { LineItem, PackageEntrySource } from "../models/CartData";
import type { ProductData } from "~/domains/product/types/ProductData";
import type { WeightData } from "~/domains/product/types/WeightData";
import { transformProduct } from "~/features/productDetails/transformers/ProductDataTransformer";
import { getNextValidDeliveryDate } from "~/common/utils/dateUtils";

/**
 * Minimal structural type accepted by `transformAddress`.
 * Covers both `CartResponseDTO.AddressDTO` (optional string fields) and the
 * global ambient `AddressDTO` (nullable string fields).
 */
type AddressInputDTO = {
  id: string;
  line1?: string | null;
  line2?: string | null;
  additionalLine1?: string | null;
  lines?: string[] | null;
  companyName?: string | null;
  postalCode: string;
  town: string;
  country: { isocode: string; name: string };
  defaultAddress: boolean;
  editable?: boolean | null;
  temporary?: boolean | null;
  province?: string | null;
  parentUnit?: string | null;
};

export interface CreditCardData {
  defaultCard: boolean;
  expMonth: string;
  expYear: string;
  expired: boolean;
  number: string;
  type: string;
  customerRefNum?: string;
}

export interface FreightPaymentsData {
  accountNumber: boolean;
  id: string;
  name: string;
}

export interface DeliveryModeData {
  code: string;
  name: string;
  freightPayments: FreightPaymentsData[];
  validOrderTypes: OrderType[];
  shippingConditionCodes: string[];
  paymentCodeInfo?: string;
  paymentCodeDisplay?: string;
}

export interface SupportedPaymentsTypesData {
  code: string;
  deliveryModes: DeliveryModeData[];
}

export interface TransformedCartData {
  accountNumber: string;
  packageEntries: number[];
  packageEntriesById: Dict<LineItem>;
  entryGroups: EntryGroup[];
  entryGroupsByNumber: Dict<EntryGroup>;
  productCodes: string[];
  products: ProductData[];
  dstError: boolean;
  comment: string;
}

export interface TransformedCheckoutData extends TransformedCartData {
  addresses: AddressData[];
  deliveryAddress: AddressData | null;
  soldTo: { name: string; accNo: string; addresses: AddressData[] };
  countriesList: { isocode: string; name: string }[];
  canAddAddress: boolean;
  unloadingPoints?: Dict<UnloadingPointData[]>;
  /** FE domain type — produced by `transformCreditCard`, NOT the raw DTO. */
  defaultCreditCard: CreditCardData | undefined;
  supportedPaymentTypes: SupportedPaymentsTypesData[];
}

// ── Public API ────────────────────────────────────────────────────────────────

export function transform(dto: CartResponseDTO): TransformedCartData {
  const weightByCode = buildWeightByCode(dto.products);
  const packageEntriesById = buildPackageEntriesById(dto.packageEntries, weightByCode);
  const packageEntries = dto.packageEntries.map((e) => e.pk);
  const entryGroupsByNumber = buildEntryGroupsByNumber(dto.entryGroups);
  const accountNumber = getAccountNumber(dto.b2bCustomerData);

  return {
    accountNumber,
    packageEntries,
    packageEntriesById,
    entryGroups: dto.entryGroups,
    entryGroupsByNumber,
    productCodes: dto.products.map((p) => p.code),
    products: dto.products.map((p) => transformProduct(p)),
    dstError: dto.dstError,
    comment: dto.affiliateComment ?? "",
  };
}

export function transformCheckout(dto: CartResponseDTO): TransformedCheckoutData {
  const base = transform(dto);

  return {
    ...base,
    addresses: transformAddresses(dto.deliveryAddressesList ?? []),
    deliveryAddress: dto.deliveryAddress ? transformAddress(dto.deliveryAddress) : null,
    soldTo: transformSoldTo(dto.soldTo),
    countriesList: dto.countriesList ?? [],
    canAddAddress: dto.canAddAddress ?? false,
    unloadingPoints: transformUnloadingPoints(dto.soldTo?.children ?? []),
    defaultCreditCard: dto.defaultCreditCard ? transformCreditCard(dto.defaultCreditCard) : undefined,
    supportedPaymentTypes: transformSupportedPaymentTypes(dto.supportedPaymentTypes ?? []),
  };
}

export function getAccountNumber(dto: CartResponseDTO["b2bCustomerData"]): string {
  return (dto && dto.sessionSelectedAccount && dto.sessionSelectedAccount.displayUid) || "";
}

// ── Internal ──────────────────────────────────────────────────────────────────

function buildWeightByCode(products: CartResponseDTO["products"]): Dict<WeightData> {
  const map: Dict<WeightData> = {};
  for (const p of products) {
    if (p.mass != null && p.massUnit != null) {
      map[p.code] = WeightUtils.convert(WeightUtils.getWeightObject(p.mass, p.massUnit), WeightUnit.kg);
    }
  }
  return map;
}

function buildPackageEntriesById(dtoEntries: PackageEntryDTO[], weightByCode: Dict<WeightData>): Dict<LineItem> {
  return toMapFn(
    dtoEntries.map((e) => transformPackageEntry(e, weightByCode)),
    (entry) => entry.pk,
  );
}

function buildEntryGroupsByNumber(groups: EntryGroup[]): Dict<EntryGroup> {
  const index: Dict<EntryGroup> = {};
  function walk(group: EntryGroup) {
    index[group.groupNumber] = group;
    group.children.forEach(walk);
  }
  groups.forEach(walk);
  return index;
}

function transformPackageEntry(dto: PackageEntryDTO, weightByCode: Dict<WeightData>): LineItem {
  const messages = [];

  const quoteExpiryDate = dto.quoteExpiryDate ? new Date(dto.quoteExpiryDate) : null;
  if (quoteExpiryDate && dayjs(quoteExpiryDate).isBefore(new Date(), "day")) {
    messages.push({
      type: "fatal" as const,
      key: "page.cart.message.error.quoteExpired",
    });
  }

  let source: PackageEntrySource = dto.source as PackageEntrySource;
  if (dto.quoteNumber) {
    source = "QUOTE" as PackageEntrySource;
  } else if (dto.contractNumber) {
    source = "CONTRACT" as PackageEntrySource;
  }

  let netPrice = undefined;
  if (dto.pnaCall !== false && dto.netPrice) {
    const surcharge = dto.surcharge ?? 0;
    const value = dto.netPrice.value;
    const baseValue = isSurchargeIncludedInPriceValue() ? value : value / (1 + surcharge);
    netPrice = {
      value,
      baseValue,
      surcharge,
      tariffSurcharge: dto.isTariffSurchargeApplicable ?? false,
      currencyIso: dto.netPrice.currencyIso,
      unit: dto.priceUnit ?? 1,
      isSubscription: false,
    };
  }

  const item: LineItem = {
    pk: dto.pk,
    id: dto.pk,
    cartEntryPk: dto.orderEntryPk,
    entryNumber: dto.entryNumber,
    productCode: dto.productCode,
    groupNumbers: dto.groupNumbers,
    quantity: dto.quantity,
    packSize: dto.packageSize,
    packCode: dto.packageCode,
    comment: dto.comment,
    deliveryDate: dto.selectedDeliveryDate
      ? new Date(dto.selectedDeliveryDate)
      : getNextValidDeliveryDate(dayjs()).toDate(),
    manuallySetRDD: dto.selectedDeliveryDateIsManual ?? false,
    weight: weightByCode[dto.productCode] ?? WeightUtils.zero(),
    netPrice,
    valid: true,
    primaryWarehouseName: dto.primaryWarehouseName ?? "",
    primaryWarehouseCode: dto.primaryWarehouseCode ?? "",
    messages,
    disableEdit: false,
    source,
    showComments: false,
    priceReference: dto.priceReference ?? "",
    edd: null,
    isInStock: null,
    internalComment: "",
    priceOverriden: false,
    contractLine: dto.contractLine ?? "",
    contractNumber: dto.contractNumber ?? "",
    quoteLine: makeQuoteLineNumber(dto.quoteLineNumber ?? null, dto.quoteScheduleLineNum ?? null),
    quoteNumber: dto.quoteNumber ?? "",
    minimumQty: dto.quoteMinQuantity ?? 1,
    isBundled: dto.isBundled ?? false,
    original: dto.original ?? true,
    estimatedShippingDate: dto.estimatedShippingDate ? new Date(dto.estimatedShippingDate) : null,
    logisticalComment: dto.logisticalComment ?? "",
    quoteExpiryDate,
    isPackagingSelectionOpen: false,
    restrictedPackage: dto.isRestrictedPackage ?? false,
    customerOrderNumber: dto.customerOrderNumber ?? "",
    countryOfOrigin: dto.originCountry ?? "",
    saleCampaignCode: dto.salesCampaignCode,
    saleCampaignEnabled: dto.specialPrice,
    actualNetPrice: dto.actualNetPrice,
    actualSalePrice: dto.actualSalePrice,
    recoPlace: dto.recommendationPlace,
    recoType: dto.recommendationType,
    tariffSurcharge: dto.isTariffSurchargeApplicable ?? false,
    targetPrice: dto.targetPrice,
  } as LineItem;

  item.valid = isCartPackEntryValid(item);
  return item;
}

function transformAddresses(dtos: AddressInputDTO[]): AddressData[] {
  return dtos.map(transformAddress);
}

export function transformAddress(dto: AddressInputDTO): AddressData {
  const lines = dto.lines ? [...dto.lines] : [];

  if (dto.line1) {
    lines.push(dto.line1);
  }

  if (dto.additionalLine1) {
    lines.push(dto.additionalLine1);
  }

  if (dto.line2) {
    lines.push(dto.line2);
  }

  return {
    id: dto.id,
    companyName: dto.companyName ?? "",
    lines,
    postalCode: dto.postalCode,
    town: dto.town,
    country: dto.country,
    default: dto.defaultAddress,
    editable: dto.editable ?? false,
    temporary: dto.temporary ?? false,
    state: dto.province ?? "",
    parentUnit: dto.parentUnit || "",
  };
}

function transformSoldTo(dto: B2BUnitDataDTO | undefined) {
  if (!dto) {
    return { name: "", accNo: "", addresses: [] };
  }
  const accNo = adjustFormat(dto.uid || "");
  return {
    name: dto.name,
    accNo,
    addresses: transformAddresses(dto.addresses ?? []),
  };
}

function transformUnloadingPoints(soldToChildren: B2BUnitDataDTO[]): Dict<UnloadingPointData[]> | undefined {
  if (!soldToChildren?.length) {
    return undefined;
  }
  const result: Dict<UnloadingPointData[]> = {};
  soldToChildren.forEach((item) => (result[item.uid] = (item.unloadingPoints || []).map(transformUnloadingPoint)));
  return result;
}

export function transformUnloadingPoint(dto: UnloadingPointDTO): UnloadingPointData {
  return {
    id: dto.id,
    name: dto.name,
    selected: dto.selected,
  };
}

function transformSupportedPaymentTypes(dtos: PaymentTypeDTO[]): SupportedPaymentsTypesData[] {
  return dtos.map(transformSupportedPaymentType);
}

function transformSupportedPaymentType(dto: PaymentTypeDTO): SupportedPaymentsTypesData {
  return {
    ...dto,
    deliveryModes: dto.deliveryModes.map(transformDeliveryMode),
  };
}

export function transformDeliveryMode(dto: DeliveryModeDataDTO): DeliveryModeData {
  return {
    code: dto.code,
    name: dto.name,
    freightPayments: dto.freightPayments,
    validOrderTypes: dto.orderTypes,
    shippingConditionCodes: dto.shippingConditionCodes,
    paymentCodeDisplay: dto.infoCode,
    paymentCodeInfo: dto.freightPayment,
  };
}

const makeQuoteLineNumber = (quoteLineNo: string | null, scheduleLineNo: string | null): string => {
  quoteLineNo = quoteLineNo ?? "";
  scheduleLineNo = scheduleLineNo ?? "";
  if (!quoteLineNo || !scheduleLineNo) {
    return "";
  }
  return `${quoteLineNo}:${scheduleLineNo}`;
};

function transformCreditCard(dto: CreditCardDataDTO): CreditCardData {
  return dto as unknown as CreditCardData;
}

export function transformUnloadingPointMap(dtos: B2BUnitDataDTO[]): Dict<UnloadingPointData[]> {
  return transformUnloadingPoints(dtos) ?? {};
}
export function transformSupporterdPaymentTypes(dto: PaymentTypeDTO): SupportedPaymentsTypesData {
  return transformSupportedPaymentType(dto);
}
